<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="card-title">Gestión de Secciones Funcionales</h5>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <label class="form-label">Filtrar por Unidad: </label>
                  <select 
                    v-model="filters.id_unidad" 
                    class="form-select" 
                    @change="handleSearch"
                  >
                    <option value="">Todas las unidades</option>
                    <option 
                      v-for="unidad in unidades" 
                      :key="unidad.id" 
                      :value="unidad.id"
                    >
                      {{ unidad.nombre_unidad }} ({{ unidad.codigo }})
                    </option>
                  </select>
                  <button 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <BButton
                v-if="hasPermission('area_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nueva Sección
              </BButton>
              <BButton
                v-if="hasPermission('area_create')"
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>
          
          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>Unidad</th>
                    <th>Sigla</th>
                    <th>Sección Funcional</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="area in areas" :key="area.id">
                    <td>{{ area.codigo_unidad }}</td>
                    <td>{{ area.codigo }}</td>
                    <td>{{ area.nombre_area }}</td>
                    <td>
                      <BBadge :variant="area.estado === 'activo' ? 'success' : 'danger'">
                        {{ area.estado }}
                      </BBadge>
                    </td>
                    <td>
                      <div class="btn-group">
                        <BButton
                          v-if="hasPermission('area_update')"
                          @click="showModal(area)"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton
                          v-if="hasPermission('area_delete')"
                          @click="confirmDelete(area)"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Paginación -->
            <div class="row mt-3" v-if="pagination.total > 0">
              <div class="col-md-12">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  @input="handlePageChange"
                  align="center"
                ></BPagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Unidad Organizacional</label>
              <select
                v-model="form.id_unidad"
                class="form-select"
                :class="{ 'is-invalid': errors.id_unidad }"
                required
              >
                <option value="">Seleccione una unidad</option>
                <option
                  v-for="unidad in unidades"
                  :key="unidad.id"
                  :value="unidad.id"
                >
                  {{ unidad.nombre_unidad }} ({{ unidad.codigo }})
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_unidad">
                {{ errors.id_unidad[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Código</label>
              <input
                type="text"
                v-model="form.codigo"
                class="form-control"
                :class="{ 'is-invalid': errors.codigo }"
                required
              />
              <div class="invalid-feedback" v-if="errors.codigo">
                {{ errors.codigo[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Nombre de la Sección Funcional</label>
              <input
                type="text"
                v-model="form.nombre_area"
                class="form-control"
                :class="{ 'is-invalid': errors.nombre_area }"
                required
              />
              <div class="invalid-feedback" v-if="errors.nombre_area">
                {{ errors.nombre_area[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Estado</label>
              <select
                v-model="form.estado"
                class="form-select"
                :class="{ 'is-invalid': errors.estado }"
                required
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
              <div class="invalid-feedback" v-if="errors.estado">
                {{ errors.estado[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BBadge, BPagination } from 'bootstrap-vue-3';

interface Area {
  id: number;
  id_unidad: number;
  codigo: string;
  nombre_area: string;
  estado: string;
  codigo_unidad: string;
}

interface Unidad {
  id: number;
  nombre_unidad: string;
  codigo: string;
}

interface FormData {
  id: number | null;
  id_unidad: string;
  codigo: string;
  nombre_area: string;
  estado: string;
}

interface Filters {
  id_unidad: string;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default {
  components: {
    BModal,
    BButton,
    BBadge,
    BPagination
  },
  setup() {
    const areas = ref<Area[]>([]);
    const loading = ref(true);
    const unidades = ref<Unidad[]>([]);
    const loadingUnidades = ref(true);
    const authStore = useAuthStore();
    const filters = ref<Filters>({
      id_unidad: ''
    });
    
    // Variables para el modal
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const loadingForm = ref(false);
    const errors = ref<Record<string, string[]>>({});

    const form = ref<FormData>({
      id: null,
      id_unidad: '',
      codigo: '',
      nombre_area: '',
      estado: 'activo'
    });

    // Paginación
    const pagination = ref<Pagination>({
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    });

    const modalTitle = computed(() => {
      return isEditing.value ? 'Editar Sección Funcional' : 'Nueva Sección Funcional';
    });

    const showModal = (area: Area | null) => {
      errors.value = {};
      if (area) {
        // Edición
        isEditing.value = true;
        form.value = {
          id: area.id,
          id_unidad: area.id_unidad.toString(),
          codigo: area.codigo,
          nombre_area: area.nombre_area,
          estado: area.estado
        };
      } else {
        // Nuevo
        isEditing.value = false;
        form.value = {
          id: null,
          id_unidad: '',
          codigo: '',
          nombre_area: '',
          estado: 'activo'
        };
      }
      modalVisible.value = true;
    };

    const handleSubmit = async () => {
      loadingForm.value = true;
      errors.value = {};
      
      try {
        let response;
        
        if (isEditing.value && form.value.id) {
          response = await axios.post(`/area/update/${form.value.id}`, form.value);
        } else {
          response = await axios.post('/area/save', form.value);
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: response.data.message
        });
        
        modalVisible.value = false;
        fetchArea();
      } catch (error: any) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Ocurrió un error al procesar la solicitud'
          });
        }
      } finally {
        loadingForm.value = false;
      }
    };

    const fetchUnidades = async () => {
      try {
        const response = await axios.get('/unidad/select');
        unidades.value = response.data.unidades;
      } catch (error) {
        console.error('Error al cargar unidades:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las unidades organizacionales'
        });
      } finally {
        loadingUnidades.value = false;
      }
    };

    const fetchArea = async () => {
      loading.value = true;
      try {
        const params: Record<string, any> = {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page
        };
        
        if (filters.value.id_unidad) {
          params.id_unidad = filters.value.id_unidad;
        }
        
        const response = await axios.get('/area/list', { params });
        areas.value = response.data.areas.data || response.data.areas;
        
        // Actualizar paginación si la API la soporta
        if (response.data.areas.meta) {
          pagination.value = {
            current_page: response.data.areas.meta.current_page,
            per_page: response.data.areas.meta.per_page,
            total: response.data.areas.meta.total,
            last_page: response.data.areas.meta.last_page
          };
        }
        
      } catch (error) {
        console.error('Error al cargar secciones:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las secciones funcionales'
        });
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.value.current_page = 1;
      fetchArea();
    };

    const handlePageChange = (page: number) => {
      pagination.value.current_page = page;
      fetchArea();
    };
    
    const confirmDelete = (area: Area) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar la sección ${area.nombre_area}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteArea(area.id);
        }
      });
    };
    
    const deleteArea = async (id: number) => {
      try {
        await axios.delete(`/area/delete/${id}`);
        Swal.fire({
          icon: 'success',
          title: 'Sección eliminada',
          text: 'La sección ha sido eliminada correctamente'
        });
        await fetchArea();
      } catch (error: any) {
        console.error('Error al eliminar sección:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar la sección'
        });
      }
    };

    const generatePDF = () => {
      // Lógica para generar PDF
      const params = filters.value.id_unidad ? { oficina: filters.value.id_unidad } : {};
      
      axios.get('/area/reporte', {
        params,
        responseType: 'blob', // Esto es crucial para manejar PDFs
        headers: {
          'Accept': 'application/pdf' // Especificamos que esperamos un PDF
        }
      }).then(response => {
        // Crear el blob correctamente
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Crear enlace y descargar
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('stream', `reporte-secciones.pdf`);
        document.body.appendChild(link);
        link.click();
        
        // Limpiar después de la descarga
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }).catch(error => {
        console.error('Error al generar PDF:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF'
        });
      });
    };
    
    const hasPermission = (permission: string) => {
      return authStore.hasPermission(permission);
    };
    
    onMounted(() => {
      fetchArea();
      fetchUnidades();
    });
    
    return {
      areas,
      unidades,
      loadingUnidades,
      filters,
      loading,
      form,
      errors,
      loadingForm,
      isEditing,
      modalTitle,
      modalVisible,
      pagination,
      handleSearch,
      handlePageChange,
      confirmDelete,
      hasPermission,
      showModal,
      handleSubmit,
      generatePDF
    };
  }
};
</script>

<style scoped>
  thead th {
    background-color: #D84315;
    color:#ffffff;
    position: sticky;
    top: 0;
  }
  tbody td {
    margin: 0px;
    padding: 8px !important;
    vertical-align: middle;
  }
  .table-responsive {
    max-height: 600px;
    overflow-y: auto;
  }
  .btn-round {
    border-radius: 50px;
  }
  .badge {
    font-size: 0.85em;
    padding: 0.35em 0.65em;
  }
</style>